import { useEffect, useState, useMemo } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerCloseButton,
    FormLabel
} from "@chakra-ui/react"
import "../../../hrm/department/Department.css"
import ProgressBar from "../../../../utils/progressBar/ProgressBar";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from 'react-select'
import { addTeleRoles, updateTeleRoles } from "../../../../redux/api/teleRoles/TeleRolesApi";
import { toTitleCase } from "../../../../utils/commonFunction/CommonFunction";
import { addTeleRolesAction, updateTeleRolesAction } from "../../../../redux/reduxSlice/TeleRolesSlice";

export function TeleRolesForm(props) {

    const { dataToEdit, setIsUpdateMode, isUpdateMode, departmentId } = props;
    const userList = useSelector((state) => state.utils.userList);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const getTeleRoledata = useSelector((state) => state.teleRole.teleRole);
    // console.log("getTeleRoledata",getTeleRoledata);
    
    const dispatch = useDispatch();
    const closeDrawer = props.closeDepartment;    
    
    // Fixed initial form data - removed useState wrapper
    const initialFormData = {
        teleSalesManager: null,
        teleSalesOwner: null,
    };
    
    const [formData, setFormData] = useState(initialFormData);

    // Function to check if teleSalesManager already exists
    const checkTeleSalesManagerExists = (teleSalesManagerId) => {
        if (!getTeleRoledata || !Array.isArray(getTeleRoledata)) {
            return false;
        }
        
        // Check if teleSalesManager already exists in the data
        const exists = getTeleRoledata.some(role => 
            role.teleSalesManager === teleSalesManagerId
        );
        
        return exists;
    };

    // Fixed useEffect for update mode
    useEffect(() => {
        console.log("Update mode:", isUpdateMode, "Data to edit:", dataToEdit);
        
        if (isUpdateMode && dataToEdit) {
            // Create a clean object with only the necessary properties
            const updateData = {
                ROWID: dataToEdit.ROWID,
                CREATEDTIME: dataToEdit.CREATEDTIME,
                MODIFIEDTIME: dataToEdit.MODIFIEDTIME,
                teleSalesManager: dataToEdit.teleSalesManager,
                teleSalesOwner: dataToEdit.teleSalesOwner,
            };
            
            // Remove null/undefined values to avoid overwriting with null
            const cleanUpdateData = Object.fromEntries(
                Object.entries(updateData).filter(([_, value]) => value != null)
            );
            
            setFormData(prev => ({
                ...prev,
                ...cleanUpdateData,
            }));
        } else {
            // Reset to initial form data when not in update mode
            setFormData(initialFormData);
        }
    }, [isUpdateMode, dataToEdit]);

    const handleOnClose = () => {
        closeDrawer();
        setFormData(initialFormData);
        setIsUpdateMode(false); // Reset update mode when closing
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.teleSalesManager) {
            toast.error("Please select Telesale Manager");
            return;
        }
        
        // Check if teleSalesManager already exists (only for new records, not updates)
        if (!isUpdateMode && checkTeleSalesManagerExists(formData.teleSalesManager)) {
            toast.error("This Telesale Manager already has a role assigned!");
            return;
        }
        
        console.log("Submitting:", formData);
        setShowProgressBar(true);
        
        addTeleRoles({ 
            ...formData, 
            createdBy: localStorage.getItem("email") 
        })
        .then((response) => {
            console.log("Response -", response);
            if (response.data.success) {
                let ROWID = response?.data?.record?.ROWID;
                dispatch(addTeleRolesAction({ ROWID, formData }));
                toast.success("Tele Sales Role added successfully!");
                handleOnClose();
            } else {
                toast.error("Unable to add Tele Sales Role, Please try again!");
            }
        })
        .catch((error) => {
            console.error("API Error", error);
            if (error.response && error.response.status === 409) {
                const errorMessage = error.response.data?.message || "Tele Sales Role already exists";
                toast.info(errorMessage);
            } else {
                toast.error("Failed to add Tele Sales Role. Please try again.");
            }
        })
        .finally(() => {
            setShowProgressBar(false);
        });
    };

    const handelOnUpdate = (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.teleSalesManager) {
            toast.error("Please select Telesale Manager");
            return;
        }
        
        // For update, check if teleSalesManager exists in OTHER records (not the current one being updated)
        if (isUpdateMode) {
            const otherRecords = getTeleRoledata.filter(role => 
                role.ROWID !== formData.ROWID
            );
            
            const existsInOtherRecords = otherRecords.some(role => 
                role.teleSalesManager === formData.teleSalesManager
            );
            
            if (existsInOtherRecords) {
                toast.error("This Telesale Manager already has a role assigned in another record!");
                return;
            }
        }
        
        setShowProgressBar(true);
        
        // Create clean data for update - only send necessary fields
        const updateData = {
            ROWID: formData.ROWID,
            teleSalesManager: formData.teleSalesManager,
            teleSalesOwner: formData.teleSalesOwner,
            MODIFIEDTIME: formData.MODIFIEDTIME,
            // Add other fields that need to be updated
        };
        
        updateTeleRoles(formData.ROWID, updateData)
        .then((response) => {
            console.log("Update response:", response);
            if (response.data.success) {
                dispatch(updateTeleRolesAction(formData));
                toast.success("Tele Sales Role Updated Successfully!");
                handleOnClose();
            }
        })
        .catch((error) => {
            console.log("Update error:", error);
            toast.error("Tele Sales Role Update Failed!");
        })
        .finally(() => {
            setShowProgressBar(false);
        });
    };
    // ... rest of your code (customComponents, customStyle, telesalesHead, teleSalesUsers) remains the same

    const customComponents = {
        MenuList: (props) => (
            <div style={{ position: 'relative' }}>
                <div {...props.innerProps} className="custom-menu-list"
                    style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        paddingBottom: '40px',
                    }}>
                    {props.children}
                </div>
                <div className="add-new-option"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        borderTop: '1px solid #ddd',
                        padding: '3px',
                        textAlign: 'center',
                    }}
                >
                </div>
            </div>
        ),
    };

    const customStyle = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? 'silver' : 'rgb(93, 135, 255)',
            borderRadius: "10px",
            border: '0.5px solid rgb(196,196,196,0.5) !important'
        }),
        placeholder: (styles) => ({
            ...styles,
            color: 'gray',
        }),
        singleValue: (styles, { data }) => ({
            ...styles,
            color: 'black',
            backgroundColor: 'white'
        }),
        option: (styles, { isSelected, isFocused }) => ({
            ...styles,
            backgroundColor: isSelected
                ? '#f1e9e9'
                : isFocused
                    ? '#f1e9e9'
                    : 'white',
            color: isSelected ? '#666' : '#333',
        })
    }

    const telesalesHead = useMemo(() => {
        const currentUserId = localStorage.getItem("userId");
        const currentUserDept = userList.find(item =>
            item?.employees?.ROWID === currentUserId
        )?.department?.departmentName?.toLowerCase()?.trim();

        // If current user is a Telesale Manager, only show themselves
        if (currentUserDept === "telesale manager") {
            const currentUser = userList.find(item =>
                item?.employees?.ROWID === currentUserId
            );

            return currentUser ? [{
                label: toTitleCase(currentUser?.employees?.employeeName || "Unknown"),
                value: currentUser?.employees?.ROWID || "",
                email: currentUser?.employees?.email || " ",
            }] : [];
        }

        // Otherwise show all Telesale Managers
        return userList
            .filter((item) => {
                const deptName = item?.department?.departmentName?.toLowerCase()?.trim() || "";
                return deptName === "telesale manager";
            })
            .map((item) => ({
                label: toTitleCase(item?.employees?.employeeName || "Unknown"),
                value: item?.employees?.ROWID || "",
                email: item?.employees?.email || " ",
            }));
    }, [userList]);



    const teleSalesUsers = useMemo(() => {
        const modulePermissions = JSON.parse(localStorage.getItem("permissions"));
        const userId = localStorage.getItem("userId");
        const userName = localStorage.getItem("name");
        const leadModule = modulePermissions?.find((module) => module.moduleName === "Lead");
        const allowedSubModules = leadModule?.subModules?.filter((sub) => sub.viewAll === true) || [];
        const hasTeleSale = allowedSubModules.some((sub) => sub.moduleName === "Tele Sale");
        const hasFieldSale = allowedSubModules.some((sub) => sub.moduleName === "Field Sale");
        const hasPreSale = allowedSubModules.some((sub) => sub.moduleName === "Pre Sale");
        const hasTelesaleManager = allowedSubModules.some((sub) => sub.moduleName === "Telesale Manager");

        const shouldDisable =
            (hasTeleSale && !hasFieldSale && !hasPreSale && !hasTelesaleManager) || // Only Tele Sale
            (hasTeleSale && hasFieldSale && !hasPreSale && !hasTelesaleManager); // Tele Sale + Field Sale

        if (shouldDisable) {
            // Disable case: return localstorage user only
            return userId && userName
                ? [
                    {
                        label: userName,
                        value: userId,
                    },
                ]
                : [];
        }

        // Enable case: return full tele sales users
        return userList
            .filter((item) => {
                const deptName = item?.department?.departmentName?.toLowerCase()?.trim() || "";
                return deptName === "tele sales" || deptName === "management" || deptName === "telesales manager";
            })
            .map((item) => ({
                label: toTitleCase(item?.employees?.employeeName || "Unknown"),
                value: item?.employees?.ROWID || "",
                email: item?.employees?.email || " ",
            }));
    }, [userList]);

    return (
        <>
            <Drawer isOpen={props.isDepartment} size='xl' onClose={handleOnClose}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>{isUpdateMode ? "Update" : "Create"} Tele Sales Role</DrawerHeader>
                    <DrawerBody>
                        {showProgressBar && <ProgressBar colorScheme='pink' />}
                        <form onSubmit={isUpdateMode ? handelOnUpdate : handleSubmit}>
                            <div className='card'>
                                <div className='card-body'>
                                    <div className='row '>
                                        <legend style={{ fontSize: 'Large' }} className='sub-headers'>Telesales Role Information</legend>
                                        <div className='col-lg-6 col-md-6 col-sm-12 mb-2'>
                                            <Select 
                                                name='teleSalesManager'
                                                options={telesalesHead} 
                                                value={telesalesHead?.find((item) => (item.value === formData?.teleSalesManager)) || null}
                                                onChange={(option) => { 
                                                    setFormData(prevFormData => ({ 
                                                        ...prevFormData, 
                                                        teleSalesManager: option?.value || null 
                                                    })) 
                                                }}
                                                styles={{
                                                    ...customStyle,
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        maxHeight: 200,
                                                        overflowY: 'auto',
                                                    }),
                                                }}
                                                components={customComponents}
                                                isClearable
                                            />
                                            <FormLabel className="small-label ps-3">Telesale Manager<span style={{ color: 'red' }}>*</span></FormLabel>
                                        </div>
                                        <div className='col-lg-6 col-md-6 col-sm-12 mb-2'>
                                            <Select 
                                                name='teleSalesOwner'
                                                options={teleSalesUsers} 
                                                value={teleSalesUsers?.find((item) => (item.value === formData?.teleSalesOwner)) || null}
                                                onChange={(option) => { 
                                                    setFormData(prevFormData => ({ 
                                                        ...prevFormData, 
                                                        teleSalesOwner: option?.value || null 
                                                    })) 
                                                }}
                                                styles={{
                                                    ...customStyle,
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        maxHeight: 200,
                                                        overflowY: 'auto',
                                                    }),
                                                }}
                                                components={customComponents}
                                                isClearable
                                            />
                                            <FormLabel className="small-label ps-3">Telesale Owner</FormLabel>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </DrawerBody>
                    <center><DrawerFooter style={{ display: 'inline-block' }} >
                        <div className='gap-3 d-flex'>
                            <button variant='outline' mr={3} onClick={handleOnClose} className="cancel-button">
                                Cancel
                            </button>
                            <button className='create-button' onClick={(e) => { !isUpdateMode ? handleSubmit(e) : handelOnUpdate(e) }} >
                                {isUpdateMode ? "Update" : "Submit"}
                            </button>
                        </div>
                    </DrawerFooter></center>
                </DrawerContent>
            </Drawer>
        </>
    )
}